import { takeRight } from 'lodash';
import { Action } from '../actions-manager/action';
import { IActionResult, IFlowApplyActionResponse } from '~/types/server.types';
import { tryParseToJsonString } from '~/utils/json.utils';

export enum FetchFlowReasons {
    initialLoad = 'initial load',
    webSocketReloadRequest = 'reload message from web socket because probably other tab has updated the flow',
    webSocketReconnect = 'web socket has reconnect',
    actionFailedInServer = 'an action has failed in the server',
    userIsBackOnline = 'user is back online',
    conflictWhenActionsBeingResolved = 'There are actions that are currently being resolved in the server so we need to make an other flow load to fetch them',
    manualFetch = 'Manual fetch',
    sentActionFailed = 'A sent action was failed in the server while the user was offline therefore we must force reload the flow to be synced.'
}

export enum ActionNumberRaiseReason {
    normalAction = 'normal action',
    groupAction = 'group action',
    multiStageAction = 'multi stage action',
    undoAction = 'undo action',
    redoAction = 'redo action'
}

export enum ActionManagerLastActionNumberReason {
    actionFailure = 'action failure',
    flowFromServer = 'flow from server'
}

export const LogEvents = {
    StartComplexAction: (
        { name, serverActionType, wasAddedToTheOfflineQueue }: Action,
        complexName: string
    ) =>
        `Start ${complexName} | name: ${name} | server type: ${serverActionType} | ${
            wasAddedToTheOfflineQueue ? 'Offline' : 'Online'
        }`,

    EndComplexAction: (
        {
            name,
            serverActionType,
            action_number,
            wasAddedToTheOfflineQueue
        }: Action,
        complexName: string
    ) =>
        `End ${complexName} | action number: ${action_number} | name: ${name} | server type: ${serverActionType} | ${
            wasAddedToTheOfflineQueue ? 'Offline' : 'Online'
        }`,

    FailedEndingComplexAction: (
        {
            name,
            serverActionType,
            action_number,
            wasAddedToTheOfflineQueue
        }: Action,
        complexName: string
    ) =>
        `Failed Ending ${complexName} | action number: ${action_number} | name: ${name} | server type: ${serverActionType} | ${
            wasAddedToTheOfflineQueue ? 'Offline' : 'Online'
        }`,

    ActionsNumbers: (description, actions) =>
        `${description} ${actions.map(x => x.action_number).join(', ')}`,

    ActionStatusResultLog: (description, actions: IActionResult[]) =>
        `${description} ${actions
            .map(x => `${x.action_number} ${x.status}`)
            .join(', ')}`,

    FailRestOfActionStack: (
        allActions: Array<Action>,
        failStartIndex: number
    ) => {
        const actions = takeRight(
            allActions,
            allActions.length - failStartIndex
        );

        return `All stack actions failed: ${actions
            .map(x => `${x.action_number}-failed`)
            .join(' | ')}`;
    },

    BeforeFlowFetch: (flowId, reason) =>
        `flow ${flowId} is about to be fetched${
            reason ? `, reason: ${reason}` : ''
        }`,

    MergingFlowResponse: (before, after, server) =>
        `About to merge flow response when action number before fetch: ${before}, after fetch: ${after}, at server: ${server}`,

    FlowLoaded: flowId => `flow ${flowId} is loaded`,
    NoChangesOnFlow:
        "The flow response wasn't loaded because there was no changes",
    LocalFlowIsAheadOfServer:
        "The flow response wasn't loaded because the local flow is ahead than the server flow",
    FlowLocalConflictWithServer:
        'The local flow last actions are different from the last actions in the server',
    FlowInServerAheadOfLocal:
        'The flow in the server is ahead of the local state so we need to reset the undo redo stack.',
    LocalResolvedFlowAheadOfServer:
        'Flow Conflict: The local resolved flow is ahead of the server flow',
    PublishedFlow: flowId => `Published flow ${flowId} successfully`,
    FlowShared: 'Shared flow successfully',
    RemovedScheduleSend: 'Removed schedule send successfully',
    FlowTestSent: 'Sent test Flow successfully',
    LogAnalyticForDev(title, allProperties) {
        const { json } = tryParseToJsonString(allProperties);

        return `${title} | properties: ${json}`;
    },

    AnalyticsActionNotFound: componentType =>
        `No action to track was found for component '${componentType}', you can add it in the defaults file or declare it using 'onAction' prop`,
    SocketEventHasNoHandler: type =>
        `The inform action of type: ${type} doesn't have handler`,
    FailedCalcScreenRes: 'Failed to calc screen resolution',
    UserOffline: 'The user if offline so now working in offline mode',
    UserBackOnline: 'The user is back online',
    ActionPushedToServerRequestQueue: (
        actionNumber: number,
        isOnline: boolean
    ) =>
        `Server Request ${
            isOnline ? 'Online' : 'Offline'
        } Queue: Action ${actionNumber} pushed.`,
    WebSocketConnected: 'Web socket has connected',
    WebSocketMessageReceived: roomName =>
        `A web socket message received at room: ${roomName}`,
    FetchFlowOfflineDismiss: 'Flow fetched is dismissed because turned offline',
    VariableRaceConditionFound: (variableId, blockId) =>
        `The server update for variable ${variableId} with the block id ${blockId} was dismissed, the reason is that it's connected block has already been deleted locally.`,
    FieldMetadataRaceConditionFound: (fieldId, blockId) =>
        `An update for the field metadata: ${fieldId} of block ${blockId} was dismissed because it was only created locally and it's server resolve haven't occur yet. The field will be updated in the next batch of actions to the server.`,
    SettingActionNumber: (action: Action, reason: ActionNumberRaiseReason) =>
        `Action number: ${
            action.action_number
        } | action number history: ${Object.keys(action.actionNumbersMap).join(
            ', '
        )} | ${action.name} | ${action._id} | ${reason}`,
    LastActionNumberFromRecover: lastActionNumber =>
        `Last action number from the recover request is: ${lastActionNumber}`,
    RetryingApplyActionsFor: 'Retrying to flush actions for:',
    ActionsGotRecovered:
        'The actions succeed in the server and the result was recovered with actions:',
    SetLastActionNumber: (
        actionNumber: number,
        reason: ActionManagerLastActionNumberReason
    ) =>
        `Action manager's last action number set to: ${actionNumber}, reason: ${reason}`,
    ApplyActionsNetworkError: retryAttemptNumber =>
        `Network error occurred, about to recover message and try on attempt #${retryAttemptNumber} for actions:`,
    RecoveredActionLog: (response: IFlowApplyActionResponse) =>
        `Recovered actions response from the server with results: ${
            response?.actions
                ?.map(x => `${x.action_number} ${x.status}`)
                .join(', ') || 'no actions'
        }`,
    ScheduledSendEmailTime: (emailDay, emailHour) =>
        `email day & hour: ${emailDay} ${emailHour}`,
    TaskDataScheduledSendDate: taskDataDate =>
        `task data scheduled send date: ${taskDataDate}`,
    SetScheduleSendSuccess: `setScheduleSend success`,
    ApplyActionErrorData: (errorType, errorData, errorMessage) =>
        `Actions failed with error type: ${errorType}, error data: ${errorData}, error message: "${errorMessage}", the failed actions:`,
    ConnectionStableStatusChange: (isStable: boolean) =>
        `The connection stable status has changed to: ${
            isStable ? 'stable' : 'not stable'
        }`
};
